import {type MediaCardType} from '@wandb/weave/common/types/media';

import {type LayoutMode} from '../common';

/**
 * Enumerating the combinations of tile counts + pagination modes.
 * Since grid mode isn't paginated, we use a relative size indicator instead.
 */
export type LoadTier =
  | 'single'
  | 'withinPageCap'
  | 'abovePageCap'
  | 'grid-small'
  | 'grid-medium'
  | 'grid-large'
  | 'unpaginated';

/**
 * Fixed numeric buckets on active tile count for cross-mode Datadog facets.
 * @see https://coreweave.atlassian.net/browse/WB-39197
 */
export type TileCountBucket =
  | '1'
  | '2-4'
  | '5-6'
  | '7-20'
  | '21-32'
  | '33-100'
  | '101+';

/** Inputs shared by pagination page-cap logic and tracking segmentation. */
export type TrackingConfigContext = {
  mediaType: MediaCardType | null;
  columnCount: number;
  layoutTileCount: number;
  activeTileCount: number;
  compareRowCount: number;
  fanOutVariable: boolean;
};

export type PaginationTileTotalContext = {
  layoutTileCount: number;
  compareRowCount: number;
  columnCount: number;
};

/** Datadog vital dimensions derived from panel layout + config (WB-39197). */
export type TrackingSegmentation = {
  activeTileCount: number;
  layoutTileCount: number;
  panelMode: LayoutMode;
  pageCap: number | null;
  loadTier: LoadTier;
  tileCountBucket: TileCountBucket;
};

export function getTileCountBucket(activeTileCount: number): TileCountBucket {
  if (activeTileCount <= 1) {
    return '1';
  }
  if (activeTileCount <= 4) {
    return '2-4';
  }
  if (activeTileCount <= 6) {
    return '5-6';
  }
  if (activeTileCount <= 20) {
    return '7-20';
  }
  if (activeTileCount <= 32) {
    return '21-32';
  }
  if (activeTileCount <= 100) {
    return '33-100';
  }
  return '101+';
}

/**
 * Load tiers for gallery and compare (paginated layouts).
 *
 * `withinPageCap` / `abovePageCap` compare total layout size to the mode page
 * cap (whether pagination is required), not the current page's active tile
 * count. `single` still reflects concurrent mounts on the current view.
 */
export function getPaginatedLoadTier(
  activeTileCount: number,
  layoutTileCount: number,
  pageCap: number | null
): LoadTier {
  if (activeTileCount <= 1) {
    return 'single';
  }
  if (pageCap != null) {
    return layoutTileCount <= pageCap ? 'withinPageCap' : 'abovePageCap';
  }
  return 'unpaginated';
}

/** Load tiers for grid mode (unpaginated, all tiles mount). */
export function getGridLoadTier(activeTileCount: number): LoadTier {
  if (activeTileCount <= 1) {
    return 'single';
  }
  if (activeTileCount <= 32) {
    return 'grid-small';
  }
  if (activeTileCount <= 100) {
    return 'grid-medium';
  }
  return 'grid-large';
}
